import React, { useEffect, useMemo, useState } from 'react';
import { FaSave, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { apiMasterAPI, equipmentAPI, productsAPI, samplingPlansAPI, settingsAPI } from '../services/api';
import { buildCleaningValidationCoverage } from '../utils/cleaningValidationCoverage';
import { Link } from 'react-router-dom';
import { Modal, ModalFooter } from '../components/Modals';
import TablePagination from '../components/TablePagination';

const SETTINGS_KEYS = {
  manualSoiling: 'worst_case_manual_soiling_entries',
};

const CleaningValidationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trains, setTrains] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [apis, setApis] = useState([]);
  const [products, setProducts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [trainMembersById, setTrainMembersById] = useState({});
  const [rankingConfig, setRankingConfig] = useState({});
  const [coverageError, setCoverageError] = useState('');
  const [manualSoilingEntries, setManualSoilingEntries] = useState([]);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    train_id: '', equipment_id: '', api_id: '', product_name: '', reason: ''
  });

  const roleLower = (() => {
    try { return String(JSON.parse(localStorage.getItem('user') || '{}')?.role || '').trim().toLowerCase(); }
    catch { return ''; }
  })();
  const isCurrentUserAdminLike = ['admin', 'super admin', 'qa'].includes(roleLower);

  const loadData = async () => {
    try {
      setLoading(true);
      const [settingsRes, trainsRes, equipmentRes, apisRes, productsRes, plansRes] = await Promise.all([
        settingsAPI.getAll(),
        equipmentAPI.getTrains(),
        equipmentAPI.getAll({ limit: 1000 }),
        apiMasterAPI.getAll({ limit: 5000 }),
        productsAPI.getAll({ limit: 5000 }),
        samplingPlansAPI.getAll()
      ]);

      const settings = settingsRes?.data?.data || {};
      setRankingConfig(settings);
      const trainRows = Array.isArray(trainsRes?.data?.data) ? trainsRes.data.data : [];
      const equipmentRows = Array.isArray(equipmentRes?.data?.data) ? equipmentRes.data.data : [];
      const apiRows = Array.isArray(apisRes?.data?.data) ? apisRes.data.data : [];
      const productRows = Array.isArray(productsRes?.data?.data) ? productsRes.data.data : [];
      const planRows = Array.isArray(plansRes?.data?.data) ? plansRes.data.data : [];

      setTrains(trainRows);
      setEquipmentList(equipmentRows);
      setApis(apiRows);
      setProducts(productRows);
      setPlans(planRows);

      const memberPairs = await Promise.all(trainRows.map(async (train) => {
        try {
          const response = await equipmentAPI.getTrainMembers(train.train_id);
          return [String(train.train_id), Array.isArray(response?.data?.data) ? response.data.data : []];
        } catch {
          return [String(train.train_id), []];
        }
      }));
      setTrainMembersById(Object.fromEntries(memberPairs));
      setCoverageError('');

      if (settings[SETTINGS_KEYS.manualSoiling]) {
        try {
          const parsed = JSON.parse(settings[SETTINGS_KEYS.manualSoiling]);
          setManualSoilingEntries(Array.isArray(parsed) ? parsed : []);
        } catch { setManualSoilingEntries([]); }
      } else {
        setManualSoilingEntries([]);
      }
    } catch (error) {
      setCoverageError(error?.response?.data?.message || 'Coverage data could not be loaded.');
      toast.error(error?.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const trainCoverage = useMemo(() => buildCleaningValidationCoverage({
    trains, apis, products, plans, trainMembersById, manualSoilingEntries, rankingConfig
  }), [trains, apis, products, plans, trainMembersById, manualSoilingEntries, rankingConfig]);

  const refreshManualSoiling = async () => {
    const settingsRes = await settingsAPI.getAll();
    const settings = settingsRes?.data?.data || {};
    if (settings[SETTINGS_KEYS.manualSoiling]) {
      try {
        const parsed = JSON.parse(settings[SETTINGS_KEYS.manualSoiling]);
        setManualSoilingEntries(Array.isArray(parsed) ? parsed : []);
      } catch { setManualSoilingEntries([]); }
    } else {
      setManualSoilingEntries([]);
    }
  };

  const saveConfiguration = async () => {
    if (!isCurrentUserAdminLike) return;
    try {
      setSaving(true);
      await settingsAPI.updateSetting(SETTINGS_KEYS.manualSoiling, JSON.stringify(manualSoilingEntries));
      toast.success('Manual contamination entries saved');
      await refreshManualSoiling();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addManualSoilingEntry = () => {
    if (!isCurrentUserAdminLike) return;
    if (!manualEntry.train_id || !manualEntry.equipment_id || !manualEntry.api_id || !manualEntry.product_name.trim()) {
      toast.error('Train, equipment, API, and product name are required');
      return;
    }
    const train = trains.find((t) => Number(t.train_id) === Number(manualEntry.train_id));
    const equipment = equipmentList.find((e) => Number(e.equipment_id) === Number(manualEntry.equipment_id));
    const belongsToTrain = (trainMembersById[String(manualEntry.train_id)] || []).some((member) => String(member.equipment_id) === String(manualEntry.equipment_id));
    if (!belongsToTrain) {
      toast.error('Select equipment that is a member of the chosen equipment train');
      return;
    }
    const api = apis.find((a) => Number(a.api_id) === Number(manualEntry.api_id));
    const entry = {
      id: Date.now(),
      train_id: Number(manualEntry.train_id),
      train_name: train?.train_name || '',
      equipment_id: Number(manualEntry.equipment_id),
      equipment_name: equipment?.equipment_name || '',
      api_id: Number(manualEntry.api_id),
      api_name: api?.api_name || '',
      product_name: manualEntry.product_name.trim(),
      reason: manualEntry.reason.trim()
    };
    setManualSoilingEntries((prev) => [...prev, entry]);
    setManualEntry({ train_id: '', equipment_id: '', api_id: '', product_name: '', reason: '' });
    setShowManualModal(false);
  };

  const removeManualSoilingEntry = (id) => {
    if (!isCurrentUserAdminLike) return;
    setManualSoilingEntries((prev) => prev.filter((entry) => Number(entry.id) !== Number(id)));
  };

  const [soilingPage, setSoilingPage] = useState(1);
  const [soilingPageSize, setSoilingPageSize] = useState(25);
  const soilingTotalPages = Math.max(1, Math.ceil(manualSoilingEntries.length / soilingPageSize));
  useEffect(() => { if (soilingPage > soilingTotalPages) setSoilingPage(soilingTotalPages); }, [soilingPage, soilingTotalPages]);
  const paginatedSoilingEntries = manualSoilingEntries.slice((soilingPage - 1) * soilingPageSize, soilingPage * soilingPageSize);
  const selectedTrainEquipmentIds = new Set((trainMembersById[String(manualEntry.train_id)] || []).map((member) => String(member.equipment_id)));

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 className="page-title">CV Coverage</h2>
          <p className="page-subtitle">Cleaning validation coverage by train, and manual contamination / soiling entries.</p>
        </div>
        {isCurrentUserAdminLike && (
          <button className="btn btn-primary" onClick={saveConfiguration} disabled={saving}>
            <FaSave /> {saving ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>

      <div className="card" style={{ marginBottom: '18px' }}>
        <div className="card-header" style={{ justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Cleaning Validation Coverage by Train</h2>
            <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>
              A train is covered only when it has an approved worst case and an approved CV sampling plan covering its equipment and applicable parts. Manual contamination is tracked separately and does not clear a CV gap.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Link className="btn btn-outline btn-sm" to="/worst-case">Worst Case</Link>
            <Link className="btn btn-primary btn-sm" to="/sampling-plans">Sampling Plans</Link>
          </div>
        </div>
        {coverageError && <div className="alert alert-danger" style={{ margin: '12px' }}>{coverageError}</div>}
        {!coverageError && trainCoverage.length === 0 ? (
          <div className="text-muted" style={{ padding: '14px' }}>No equipment trains are available in the current unit/section.</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Equipment Train</th>
                  <th>Train Equipment</th>
                  <th>Worst Case</th>
                  <th>Approved CV Plan</th>
                  <th>CV Equipment / Part Coverage</th>
                  <th>Manual Contamination</th>
                  <th>Coverage Status / Gaps</th>
                </tr>
              </thead>
              <tbody>
                {trainCoverage.map(({ train, members, worstCase, approvedCvPlans, worstCaseCvPlans, missingEquipment, soilingForTrain, gaps }) => (
                  <tr key={train.train_id}>
                    <td>
                      <Link to="/equipment-trains" state={{ openViewTrainId: train.train_id }} className="record-link" title="View train">
                        <strong>{train.train_name || train.train_code}</strong>
                      </Link>
                      <div className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>{train.train_code}</div>
                    </td>
                    <td>{members.length} equipment item(s)</td>
                    <td>{worstCase && !worstCase.is_provisional
                      ? <>{worstCase.product_name || 'Product not identified'}<div className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>API: {worstCase.api_name || '—'}</div></>
                      : <span style={{ color: 'var(--warning-text)', fontWeight: 600 }}>No approved worst case</span>}</td>
                    <td>{worstCaseCvPlans.length
                      ? worstCaseCvPlans.map((plan) => <div key={plan.plan_id}>{plan.plan_code || `Plan ${plan.plan_id}`}</div>)
                      : approvedCvPlans.length
                        ? <span style={{ color: 'var(--warning-text)' }}>Approved plan uses a different API</span>
                      : <span style={{ color: 'var(--warning-text)', fontWeight: 600 }}>No approved CV plan</span>}</td>
                    <td>{members.length === 0
                      ? <span style={{ color: 'var(--warning-text)' }}>No equipment linked to train</span>
                      : missingEquipment.length === 0 && worstCaseCvPlans.length
                        ? <span style={{ color: 'var(--success-text)', fontWeight: 600 }}>All linked equipment/parts covered</span>
                        : <span title={missingEquipment.map((member) => member.equipment_name || member.equipment_code).join(', ')} style={{ color: 'var(--warning-text)' }}>
                          {worstCaseCvPlans.length ? `${missingEquipment.length} item(s) missing locations` : 'Coverage not established'}
                          {missingEquipment.length > 0 && <div className="text-muted" style={{ fontSize: 'var(--fs-xs)', maxWidth: '240px' }}>{missingEquipment.map((member) => member.equipment_name || member.equipment_code).join(', ')}</div>}
                        </span>}</td>
                    <td>{soilingForTrain.length
                      ? <span style={{ color: 'var(--warning-text)' }}>{soilingForTrain.length} logged entr{soilingForTrain.length === 1 ? 'y' : 'ies'} (separate from CV)</span>
                      : <span className="text-muted">None logged</span>}</td>
                    <td>{gaps.length === 0 && members.length > 0
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: 'var(--success-text)', fontWeight: 700 }}><FaCheckCircle /> Covered</span>
                      : <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: '5px', color: 'var(--warning-text)', fontWeight: 600 }}><FaExclamationTriangle style={{ marginTop: '2px', flexShrink: 0 }} /><span>{members.length === 0 ? 'Link equipment to this train' : gaps.join('; ')}</span></span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header" style={{ justifyContent: 'space-between' }}>
          <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Manual Contamination / Soiling Entries</h2>
          {isCurrentUserAdminLike && (
            <button className="btn btn-warning" type="button" onClick={() => setShowManualModal(true)}>
              Manual Contamination / Soiling
            </button>
          )}
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Equipment Train</th>
                <th>Equipment</th>
                <th>API</th>
                <th>Product Name</th>
                <th>Reason</th>
                {isCurrentUserAdminLike && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {manualSoilingEntries.length === 0 ? (
                <tr><td colSpan={isCurrentUserAdminLike ? 6 : 5} className="text-center text-muted">No manual contamination entries</td></tr>
              ) : paginatedSoilingEntries.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.train_name || '-'}</td>
                  <td>{entry.equipment_name || '-'}</td>
                  <td>{entry.api_name || '-'}</td>
                  <td>{entry.product_name || '-'}</td>
                  <td>{entry.reason || '-'}</td>
                  {isCurrentUserAdminLike && (
                    <td>
                      <button className="btn btn-sm btn-danger-outline" type="button" onClick={() => removeManualSoilingEntry(entry.id)}>
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {manualSoilingEntries.length > 0 && (
          <TablePagination total={manualSoilingEntries.length} page={soilingPage} pageSize={soilingPageSize} onPageChange={setSoilingPage} onPageSizeChange={(n) => { setSoilingPageSize(n); setSoilingPage(1); }} itemLabel="entries" />
        )}
      </div>

      {showManualModal && (
        <Modal isOpen size="lg" title="Manual Contamination / Soiling Entry" onClose={() => setShowManualModal(false)}>
            <div className="modal-section">
              <h3 className="modal-section-title">Equipment Scope</h3>
              <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Equipment Train *</label>
                <select className="form-control" value={manualEntry.train_id} onChange={(e) => setManualEntry((prev) => ({ ...prev, train_id: e.target.value, equipment_id: '' }))}>
                  <option value="">Select train</option>
                  {trains.map((train) => (
                    <option key={train.train_id} value={train.train_id}>{train.train_name || train.train_code}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Equipment *</label>
                <select className="form-control" value={manualEntry.equipment_id} onChange={(e) => setManualEntry((prev) => ({ ...prev, equipment_id: e.target.value }))}>
                  <option value="">Select equipment</option>
                  {equipmentList.filter((eq) => selectedTrainEquipmentIds.has(String(eq.equipment_id))).map((eq) => (
                    <option key={eq.equipment_id} value={eq.equipment_id}>{eq.equipment_code} - {eq.equipment_name}</option>
                  ))}
                </select>
              </div>
              </div>
            </div>
            <div className="modal-section">
              <h3 className="modal-section-title">Product &amp; Reason</h3>
              <div className="form-grid">
              <div className="form-group">
                <label className="form-label">API *</label>
                <select className="form-control" value={manualEntry.api_id} onChange={(e) => setManualEntry((prev) => ({ ...prev, api_id: e.target.value }))}>
                  <option value="">Select API</option>
                  {apis.map((api) => (
                    <option key={api.api_id} value={api.api_id}>{api.api_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Product Name *</label>
                <input className="form-control" value={manualEntry.product_name} onChange={(e) => setManualEntry((prev) => ({ ...prev, product_name: e.target.value }))} placeholder="Enter product name" />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Reason</label>
                <textarea className="form-control" rows={3} value={manualEntry.reason} onChange={(e) => setManualEntry((prev) => ({ ...prev, reason: e.target.value }))} placeholder="Enter reason for manual contamination/soiling coverage" />
              </div>
              </div>
            </div>
          <ModalFooter>
              <button type="button" className="btn btn-outline" onClick={() => setShowManualModal(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={addManualSoilingEntry}>Add Entry</button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default CleaningValidationSettings;
